import { Section } from '~/components/layout';
import { ButtonLink, DigitReel, FRAC_DELAY_MS } from '~/components/ui';
import { rewardsHero } from '~/content/rewards';
import { getAppCta } from '~/content/navigation';

// The headline reward rate lives inside the hero title as "4.2%". On page load
// the two DIGITS roll upward, odometer-style, each in its own reel — while the
// decimal point and the "%" stay perfectly still.
const RATE_TOKEN = '4.2%';
const RATE_VALUE = '4.2';
const [INT_DIGIT, FRAC_DIGIT] = RATE_VALUE.split('.').map(Number);

/**
 * Rewards page hero: centred headline + "Get the app" CTA.
 *
 * Matches the shared {@link Hero}'s heading→CTA spacing (mt-14) so the gap is
 * pixel-consistent with /accounts, /cards, etc. (this page has no hero image, so
 * it doesn't use the full shared Hero, only its title-block spacing token).
 *
 * The "4.2%" figure animates as two per-digit reels ("4" and "2"). The decimal
 * point and the "%" are static text that never transforms. Reduced-motion users
 * get "4.2%" instantly (the reels are server-rendered on their final digits).
 */
export function RewardsHero() {
  const [before, after] = rewardsHero.title.split(RATE_TOKEN);

  return (
    <Section className="pt-16 !pb-1 md:pt-24 md:!pb-8">
      <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
        <h1 className="text-[40px] leading-[1.1] font-semibold tracking-[-0.03em] text-balance md:text-title-m">
          {before}
          {/* Static value for assistive tech; the animated reels are decorative. */}
          <span className="sr-only">{RATE_VALUE}</span>
          <DigitReel target={INT_DIGIT} />
          {/* Static decimal point — never animates. */}
          <span aria-hidden>.</span>
          <DigitReel target={FRAC_DIGIT} delayMs={FRAC_DELAY_MS} />
          {`%${after}`}
        </h1>
        <p className="mt-6 max-w-2xl text-body text-ink-secondary">{rewardsHero.subtitle}</p>
        <ButtonLink href={getAppCta.href} size="md" className="mt-9 md:mt-14">
          {getAppCta.label}
        </ButtonLink>
      </div>
    </Section>
  );
}
